import { useNavigate } from 'react-router-dom';
import {
  FiTarget, FiCheckCircle, FiBookmark, FiEdit3, FiBarChart2, FiClipboard,
} from 'react-icons/fi';

const CYCLE_COLORS = ['#a855f7', '#6366f1', '#3b82f6', '#0ea5e9', '#10b981'];

const lineProps = (steps, colors) => {
  const N = steps.length;
  const sw = 100 / N;
  const start = sw / 2;

  const lastDoneIdx = steps.reduce((a, s, i) => (s.completed ? i : a), -1);
  const nextIdx = steps.findIndex(s => s.current);

  const solidEnd = lastDoneIdx >= 0 ? (lastDoneIdx + 0.5) * sw : start;
  const solidW = Math.max(0, solidEnd - start);
  const dashEnd = nextIdx >= 0 ? (nextIdx + 0.5) * sw : solidEnd;
  const dashW = Math.max(0, dashEnd - solidEnd);

  return {
    start,
    end: 100 - sw / 2,
    solidW,
    dashW,
    dashStart: start + solidW,
    solidFrom: lastDoneIdx >= 0 ? colors[0] : '#e5e7eb',
    solidTo: lastDoneIdx >= 0 ? colors[Math.min(lastDoneIdx, colors.length - 1)] : '#e5e7eb',
    dashColor: nextIdx >= 0 ? colors[Math.min(nextIdx, colors.length - 1)] : '#d1d5db',
  };
};

const JobSearchJourney = ({ userStatus }) => {
  const { latestResumeId, recentJobsForCycle = [], latestSavedJobId } = userStatus || {};
  const navigate = useNavigate();

  const buildCycleSteps = (job) => [
    {
      name: 'Save Jobs',
      completed: true,
      current: false,
      icon: <FiBookmark className="h-4 w-4" />,
      path: job?.id ? `/jobs/${job.id}/apply` : '/jobs/saved',
    },
    {
      name: 'Check Fit',
      completed: !!job?.hasCheckedFit,
      current: !job?.hasCheckedFit,
      icon: <FiBarChart2 className="h-4 w-4" />,
      path: job?.id ? `/jobs/${job.id}/apply` : '/jobs/saved',
    },
    {
      name: 'Polish',
      completed: !!job?.hasPolished || !!job?.hasApplied,
      current: !!job?.hasCheckedFit && !job?.hasPolished && !job?.hasApplied,
      icon: <FiEdit3 className="h-4 w-4" />,
      path: job?.id ? `/jobs/${job.id}/apply` : (latestResumeId ? `/resumes/${latestResumeId}/polish` : '/resumes'),
    },
    {
      name: 'Apply',
      completed: !!job?.hasApplied,
      current: (!!job?.hasPolished || !!job?.hasCheckedFit) && !job?.hasApplied,
      icon: <FiTarget className="h-4 w-4" />,
      path: job?.id ? `/jobs/${job.id}/apply` : '/jobs/saved',
    },
    {
      name: 'Track Apps',
      completed: !!job?.hasTracked,
      current: !!job?.hasApplied && !job?.hasTracked,
      icon: <FiClipboard className="h-4 w-4" />,
      path: '/applications',
    },
  ];

  const StepRow = ({ steps }) => {
    const lp = lineProps(steps, CYCLE_COLORS);
    return (
      <div className="relative">
        <div
          className="absolute h-px bg-gray-100"
          style={{ top: 18, left: `${lp.start}%`, right: `${100 - lp.end}%`, zIndex: 0 }}
        />
        {lp.solidW > 0 && (
          <div
            className="absolute h-0.5 transition-all duration-500"
            style={{
              top: 17,
              left: `${lp.start}%`,
              width: `${lp.solidW}%`,
              background: `linear-gradient(to right, ${lp.solidFrom}, ${lp.solidTo})`,
              zIndex: 1,
            }}
          />
        )}
        {lp.dashW > 0 && (
          <div
            className="absolute"
            style={{
              top: 17,
              left: `${lp.dashStart}%`,
              width: `${lp.dashW}%`,
              height: 2,
              zIndex: 1,
              background: `repeating-linear-gradient(to right, ${lp.dashColor} 0px, ${lp.dashColor} 4px, transparent 4px, transparent 8px)`,
            }}
          />
        )}

        <div className="flex items-start justify-between">
          {steps.map((step, idx) => {
            const color = CYCLE_COLORS[Math.min(idx, CYCLE_COLORS.length - 1)];
            const done = step.completed;
            const next = step.current;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => navigate(step.path)}
                className="relative flex flex-col items-center flex-1 group cursor-pointer focus:outline-none"
              >
                <div className="relative">
                  {done ? (
                    <div
                      className="z-10 flex items-center justify-center w-8 h-8 rounded-full text-white transition-transform group-hover:scale-110"
                      style={{ background: color }}
                    >
                      <FiCheckCircle className="h-3.5 w-3.5" />
                    </div>
                  ) : next ? (
                    <div
                      className="z-10 flex items-center justify-center w-8 h-8 rounded-full transition-transform group-hover:scale-110"
                      style={{ border: `2px dashed ${color}`, background: `${color}15`, color }}
                    >
                      {step.icon}
                    </div>
                  ) : (
                    <div className="z-10 flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-300 transition-transform group-hover:scale-110">
                      {step.icon}
                    </div>
                  )}
                  {next && (
                    <span
                      className="absolute -top-1.5 -right-1 rounded-full font-bold text-white pointer-events-none"
                      style={{ fontSize: 6.5, lineHeight: 1, background: color, padding: '2px 4px', zIndex: 20 }}
                    >
                      Next
                    </span>
                  )}
                </div>

                <span
                  className="mt-1 text-center leading-tight group-hover:underline"
                  style={{ fontSize: 10, fontWeight: next ? 700 : 500, color: done ? '#6b7280' : next ? '#111827' : '#9ca3af' }}
                >
                  {step.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const cycleJobs = recentJobsForCycle.length > 0
    ? recentJobsForCycle
    : latestSavedJobId
      ? [{ id: latestSavedJobId, title: '', company: '', hasCheckedFit: false, hasPolished: false, hasApplied: false, hasTracked: false }]
      : [];

  if (cycleJobs.length === 0) {
    return (
      <div className="bg-white shadow-sm rounded-xl overflow-hidden border-l-4 border-violet-500 bg-violet-50/30">
        <div className="px-4 py-3 sm:px-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Job Search Cycles</h3>
            <div
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full border whitespace-nowrap"
              style={{ fontSize: 10, fontWeight: 600, color: '#7c3aed', background: '#f5f3ff', borderColor: '#a78bfa' }}
            >
              ↺ Repeat per job
            </div>
          </div>

          <div
            style={{
              background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 60%, #faf5ff 100%)',
              borderRadius: 10,
              border: '1px dashed #c4b5fd',
              padding: '14px 16px',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: '0 0 2px', fontSize: 12, fontWeight: 700, color: '#4c1d95' }}>
              🔍 Start Your Job Search
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 10, color: '#7c3aed', lineHeight: 1.5 }}>
              Save jobs from LinkedIn, Indeed, or company career pages with one click.
            </p>

            <a
              href="https://chromewebstore.google.com/detail/resumemo-job-saver/ikkpnjimfefekoncejhmidjdccllhcej"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '7px 14px',
                borderRadius: 20,
                marginBottom: 10,
                background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                textDecoration: 'none',
                boxShadow: '0 3px 10px rgba(124,58,237,0.35)',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 5px 16px rgba(124,58,237,0.48)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '0 3px 10px rgba(124,58,237,0.35)';
              }}
            >
              🔌 Install Chrome Extension
            </a>

            <p style={{ margin: '0 0 6px', fontSize: 9, fontWeight: 600, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              then browse jobs on
            </p>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                { label: 'LinkedIn', href: 'https://www.linkedin.com/jobs/', color: '#0a66c2' },
                { label: 'Indeed', href: 'https://www.indeed.com/', color: '#2557a7' },
                { label: 'Glassdoor', href: 'https://www.glassdoor.com/Job/', color: '#0caa41' },
              ].map(({ label, href, color }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '4px 10px',
                    borderRadius: 12,
                    fontSize: 10,
                    fontWeight: 600,
                    textDecoration: 'none',
                    border: `1px solid ${color}30`,
                    background: `${color}10`,
                    color,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `${color}10`;
                  }}
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden border-l-4 border-violet-500 bg-violet-50/30">
      <div className="px-4 py-3 sm:px-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Job Search Cycles</h3>
          <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-full border whitespace-nowrap"
            style={{ fontSize: 10, fontWeight: 600, color: '#7c3aed', background: '#f5f3ff', borderColor: '#a78bfa' }}
          >
            ↺ Repeat per job
          </div>
        </div>

        {cycleJobs.map((job, i) => (
          <div key={i} className={i > 0 ? 'mt-2.5 pt-2.5 border-t border-gray-100' : ''}>
            {(job?.title || job?.company) && (
              <div
                className="mb-2 truncate"
                style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}
                title={[job?.title, job?.company].filter(Boolean).join(' at ')}
              >
                {[job?.title, job?.company].filter(Boolean).join(' at ')}
              </div>
            )}
            <StepRow steps={buildCycleSteps(job)} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobSearchJourney;
